<template>
  <div>
    <h4 class="tw-text-[#6d7287] tw-text-base tw-mb-5">
      {{ t('suppliers.titles.edit_supplier') }}
    </h4>

    <Card class="tw-p-8">
      <div
        class="tw-text-base tw-font-medium tw-mb-6 tw-uppercase tw-text-[#3f4456] tw-pb-4 tw-border-b tw-border-[#d7ddf5]"
      >
        {{ t('suppliers.titles.supplier_info') }}
      </div>

      <form @submit="onSubmitSupplierForm" class="tw-flex-1 tw-w-full tw-px-4 tw-flex tw-flex-col">
        <!-- Number and Name -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label for="supplierNumber">
              {{ t('suppliers.titles.supplier_number') }}
              <span class="tw-text-red-600">*</span>
            </Label>
            <Input
              v-model="supplierNumber"
              id="supplierNumber"
              name="supplierNumber"
              type="text"
              :class="{ 'tw-border-danger': errors.supplierNumber }"
              :placeholder="t('suppliers.titles.number_placeholder') + '...'"
            />
            <div v-if="errors.supplierNumber" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.supplierNumber }}
            </div>
          </div>

          <div>
            <Label for="supplierName">
              {{ t('suppliers.titles.supplier_name') }}
              <span class="tw-text-red-600">*</span>
            </Label>
            <Input
              v-model="supplierName"
              id="supplierName"
              name="supplierName"
              type="text"
              :class="{ 'tw-border-danger': errors.supplierName }"
              :placeholder="t('suppliers.titles.name_placeholder') + '...'"
            />
            <div v-if="errors.supplierName" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.supplierName }}
            </div>
          </div>
        </div>

        <!-- Address and Zip code -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label for="address">
              {{ t('suppliers.titles.address') }}
            </Label>
            <Input
              v-model="address"
              id="address"
              name="address"
              type="text"
              :class="{ 'tw-border-danger': errors.address }"
              :placeholder="t('suppliers.titles.address_placeholder') + '...'"
            />
            <div v-if="errors.address" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.address }}
            </div>
          </div>

          <div>
            <Label for="zipCode">
              {{ t('suppliers.titles.zip_code') }}
            </Label>
            <Input
              v-model="zipCode"
              id="zipCode"
              name="zipCode"
              type="text"
              :class="{ 'tw-border-danger': errors.zipCode }"
              :placeholder="t('suppliers.titles.zip_code_placeholder') + '...'"
            />
            <div v-if="errors.zipCode" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.zipCode }}
            </div>
          </div>
        </div>

        <!-- Country and City  -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label for="country">{{ t('suppliers.titles.country') }}</Label>
            <SearchableSelect
              v-model="country"
              :options="countriesList"
              :loading="countriesLoading"
              value-prop="name"
              label-prop="displayed_name"
              :placeholder="t('suppliers.titles.country_placeholder') + '...'"
              :class="{ 'tw-border-danger': errors.country }"
            />
            <div v-if="errors.country" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.country }}
            </div>
          </div>

          <div>
            <Label for="city">
              {{ t('suppliers.titles.city') }}
            </Label>
            <SearchableSelect
              v-model="city"
              :no-options-text="
                country ? t('supplier.data.no_cities') : t('suppliers.titles.select_country_error')
              "
              :options="citiesList"
              :loading="citiesLoading"
              value-prop="name"
              label-prop="name"
              :placeholder="t('suppliers.titles.city_placeholder') + '...'"
              :class="{ 'tw-border-danger': errors.city }"
            />
            <div v-if="errors.city" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.city }}
            </div>
          </div>
        </div>

        <!-- Phone and Email -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label for="phone">{{ t('suppliers.titles.phone') }}</Label>
            <PhoneInput
              v-model="phone"
              :placeholder="t('suppliers.titles.phone_placeholder')"
              code="DE"
              :disabled="false"
              :class="{ 'tw-border-danger': errors.phone }"
            />
            <div v-if="errors.phone" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.phone }}
            </div>
          </div>

          <div>
            <Label for="supplierEmail">
              {{ t('suppliers.titles.email') }}
            </Label>
            <Input
              v-model="supplierEmail"
              id="supplierEmail"
              name="supplierEmail"
              type="email"
              :class="{ 'tw-border-danger': errors.supplierEmail }"
              :placeholder="t('suppliers.titles.email_placeholder') + '...'"
            />
            <div v-if="errors.supplierEmail" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ errors.supplierEmail }}
            </div>
          </div>
        </div>

        <div v-if="supplierUpdatedSuccessfully != null" class="result-alert tw-mt-4">
          <Alert v-if="supplierUpdatedSuccessfully" class="lg:tw-w-7/12">
            <AlertDescription>{{ t('suppliers.titles.update_success') }}</AlertDescription>
          </Alert>
          <Alert v-if="!supplierUpdatedSuccessfully" variant="destructive" class="lg:tw-w-7/12">
            <AlertDescription>{{ errorMessage }}</AlertDescription>
          </Alert>
        </div>

        <div class="tw-flex tw-gap-2 tw-justify-end">
          <Button
            type="button"
            variant="secondary"
            @click="backToSuppliersList"
            :disabled="isSubmitting"
          >
            {{ t('common.actions.discard') }}
          </Button>
          <CustomerActionButton
            type="submit"
            class="tw-bg-primary-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm"
            :disabled="isSubmitting"
          >
            {{ t('suppliers.titles.edit') }}
          </CustomerActionButton>
        </div>
      </form>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch, type Ref } from 'vue';
import router from '@/router';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { PhoneInput } from '@/components/ui/phone-input';

import { useSupplierStore } from '@/stores/supplier.store';

import { useSupplierForm } from '@/_helpers/composables/master-data/supplier-changing.composable';
import { useWorldStore } from '@/stores/world.store';
import { useCustomerStore } from '@/stores/customer.store';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';

import type { Supplier } from '@/_helpers/interfaces/suppliers/supplier.interface';
import { useI18n } from 'vue-i18n';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import cookieService from '@/_helpers/services/cookie.service';

const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();
const props = defineProps({
  supplierId: String, // Route param
});

const {
  errors,
  handleSubmit,
  isSubmitting,
  setValues,
  supplierNumber,
  supplierName,
  address,
  zipCode,
  country,
  city,
  setCityValue,
  resetCity,
  phone,
  supplierEmail,
  countriesList,
  citiesList,
  countriesLoading,
  backToSuppliersList,
} = useSupplierForm();

const { editSupplier, getSupplierById } = useSupplierStore();
const { getCitiesListByCountry } = useWorldStore();
const { getCustomersOptions } = useCustomerStore();

const currentSupplier: Ref<Supplier | null> = ref(null);

const supplierUpdatedSuccessfully: Ref<boolean | null> = ref(null);
const errorMessage = ref('');
const citiesLoading = ref(false);
let customersList: any = ref([]);

const onSubmitSupplierForm = handleSubmit(async values => {
  try {
    await editSupplier(+props.supplierId!, {
      supplier_number: values.supplierNumber,
      name: values.supplierName,
      address: values.address,
      zip_code: values.zipCode!,
      city: values.city!,
      country: values.country!,
      phone_number: values.phone,
      email: values.supplierEmail,
      customer: values.customer!,
    });

    backToSuppliersList();
  } catch (error: any) {
    supplierUpdatedSuccessfully.value = false;
    errorMessage.value = error.response.data.errors[0].detail;
  }
});

onMounted(async () => {
  validateRouteParam();
  const selectedCustomerId = cookieService.get('selectedCustomerId');

  currentSupplier.value = await getSupplierById(+props.supplierId!);

  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.suppliers',
      to: { name: 'list-suppliers' },
    },
    {
      label: currentSupplier.value?.name,
    },
    {
      labelKey: 'common.forms.edit',
    },
  ]);

  customersList.value = await getCustomersOptions();
  if (currentSupplier.value?.customer !== parseInt(selectedCustomerId!)) {
    router.push({ name: 'list-suppliers' });
  }
  setValues({
    supplierNumber: currentSupplier.value?.supplier_number,
    supplierName: currentSupplier.value?.name,
    address: currentSupplier.value?.address,
    zipCode: currentSupplier.value?.zip_code,
    country: currentSupplier.value?.country,
    phone: currentSupplier.value?.phone_number || '',
    supplierEmail: currentSupplier.value?.email,
    customer: currentSupplier.value?.customer!,
  });

  await nextTick();

  setCityValue(currentSupplier.value.city as any);
});

watch(country, async (selectedCountry: string | null) => {
  resetCity();
  if (selectedCountry) {
    citiesLoading.value = true;
    try {
      citiesList.value = await getCitiesListByCountry(selectedCountry);
    } finally {
      citiesLoading.value = false;
    }
  }
});

const validateRouteParam = () => {
  if (isNaN(+props.supplierId!)) {
    // TODO: navigate to not found page
    router.push({ name: 'list-suppliers' });
  }
};
</script>

<style scoped></style>
